import React, { useState, useEffect, useCallback } from "react";
import { Modal, Button, Form, Alert, Spinner } from "react-bootstrap";
import { useStudents } from "../../../hooks/students/useStudents";
import {
  useCSVStudentData,
  CSVStudent,
} from "../../../hooks/CSV/students/useCSVStudentData";
import { useImportHistory } from "../../../hooks/CSV/students/useImportHistoryStudent";
import { importHistoryStudentAPI } from "../../../hooks/importHistory/importHistoryStudentAPI";
import { ImportHistoryTable } from "../list/CSV/ImportHistoryTable";
import { CSVStudentPreviewTable } from "../list/CSV/CSVStudentPreviewTable";
import { InvalidDataTable } from "../list/CSV/InvalidDataTable";
import ErrorDetailsModal from "../list/CSV/ErrorDetailsModal";
import { CSVStudentProcessingError } from "../../../utils/csv/csv-validatorStudent";

type CSVStudentManagementViewProps = {
  token: string;
  onClose: () => void;
  refetch: () => void;
};

type ErrorDetail = {
  student: CSVStudent;
  error: string;
  is_restore?: boolean;
  restored?: boolean;
  id: string;
};

export function CSVStudentManagementView({
  token,
  onClose,
  refetch,
}: CSVStudentManagementViewProps) {
  const {
    selectedFile,
    setSelectedFile,
    CSVStudentError,
    CSVStudentPreviewData,
    invalidData,
    fileInputKey,
    setFileInputKey,
    validateStudent,
    handleCSVStudentFileUpload,
    setCSVStudentPreviewData,
    setInvalidData,
  } = useCSVStudentData();

  const {
    importHistory,
    isFetchingHistory,
    fetchImportHistory,
    setImportHistory,
  } = useImportHistory(token);

  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editingStudent, setEditingStudent] = useState<CSVStudent | null>(null);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorDetails, setErrorDetails] = useState<ErrorDetail[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>("");
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [processError, setProcessError] = useState<string | null>(null);

  const {
    processCSVStudent,
    isProcessingCSVStudent,
    CSVStudentError: processCSVStudentError,
  } = useStudents(token);

  useEffect(() => {
    fetchImportHistory();
  }, [fetchImportHistory]);

  const handleSaveImportError = useCallback(async () => {
    const errorImportStatusData = {
      file_name: selectedFile?.name || "Desconhecido",
      timestamp: new Date().toISOString(),
      status: "finished" as const,
      progress: 0,
      table_name: "students",
      total: CSVStudentPreviewData.length,
      success: 0,
      detailed_errors: [] as {
        id: string;
        student: CSVStudent;
        error: string;
        is_restore?: boolean;
        restored?: boolean;
      }[],
      error: "Erro no processamento",
    };

    try {
      const errorImportStatus = await importHistoryStudentAPI.create(
        errorImportStatusData,
        token
      );
      setImportHistory((prev: any) => [errorImportStatus, ...prev]);
    } catch (saveError) {
      console.error(
        "Erro ao salvar histórico de importação com falha:",
        saveError
      );
    }
  }, [selectedFile, CSVStudentPreviewData.length, token, setImportHistory]);

  const handleProcessImportInBackground = useCallback(async () => {
    if (!selectedFile || CSVStudentPreviewData.length === 0) return;

    try {
      const result = await processCSVStudent(CSVStudentPreviewData);

      const newImportStatusData = {
        file_name: selectedFile.name,
        timestamp: new Date().toISOString(),
        status: "finished" as const,
        progress: result.success,
        total: CSVStudentPreviewData.length,
        table_name: "students",
        success: result.success,
        detailed_errors: result.errors.map(
          (error: CSVStudentProcessingError) => ({
            id: error.id,
            student: error.student,
            error: error.error,
            is_restore: error.student.is_restore,
            restored: false,
          })
        ),
        error:
          result.errors.length > 0
            ? `${result.errors.length} erro(s) ao importar`
            : undefined,
      };

      const newImportStatus = await importHistoryStudentAPI.create(
        newImportStatusData,
        token
      );
      setImportHistory((prev: any) => [newImportStatus, ...prev]);

      setSelectedFile(null);
      setCSVStudentPreviewData([]);
      setInvalidData([]);
      setFileInputKey(Date.now());

      setShowSuccessMessage(true);
      setTimeout(() => {
        setShowSuccessMessage(false);
        onClose();
        refetch();
      }, 3250);
    } catch (error) {
      console.error("Erro ao processar importação:", error);
      setProcessError("Erro ao processar importação.");
      handleSaveImportError();
    }
  }, [
    selectedFile,
    CSVStudentPreviewData,
    processCSVStudent,
    token,
    refetch,
    handleSaveImportError,
    setImportHistory,
    setSelectedFile,
    setCSVStudentPreviewData,
    setInvalidData,
    setFileInputKey,
    onClose,
  ]);

  const handleStudentRestored = useCallback((id: string) => {
    setErrorDetails((prevErrorDetails) =>
      prevErrorDetails.map((errorDetail) =>
        errorDetail.id === id
          ? { ...errorDetail, restored: true, is_restore: false }
          : errorDetail
      )
    );
    setShowSuccessMessage(true);
    setTimeout(() => setShowSuccessMessage(false), 3000);
  }, []);

  const handleRemovePreviewItem = useCallback(
    (index: number) => {
      setCSVStudentPreviewData((prev) =>
        prev.filter((_, idx) => idx !== index)
      );
    },
    [setCSVStudentPreviewData]
  );

  const handleRemoveInvalidItem = useCallback(
    (index: number) => {
      setInvalidData((prev) => prev.filter((_, idx) => idx !== index));
    },
    [setInvalidData]
  );

  const handleEditInvalidItem = useCallback(
    (index: number) => {
      setEditingIndex(index);
      setEditingStudent({ ...invalidData[index] });
    },
    [invalidData]
  );

  const handleCancelEdit = useCallback(() => {
    setEditingIndex(null);
    setEditingStudent(null);
  }, []);

  const handleSaveEdit = useCallback(() => {
    if (editingStudent !== null && editingIndex !== null) {
      const validatedStudent = validateStudent(editingStudent);
      if (validatedStudent.isValid) {
        setCSVStudentPreviewData((prev) => [...prev, validatedStudent]);
        setInvalidData((prev) => prev.filter((_, idx) => idx !== editingIndex));
      } else {
        setInvalidData((prev) =>
          prev.map((item, idx) =>
            idx === editingIndex ? validatedStudent : item
          )
        );
      }
      setEditingIndex(null);
      setEditingStudent(null);
    }
  }, [
    editingStudent,
    editingIndex,
    validateStudent,
    setCSVStudentPreviewData,
    setInvalidData,
  ]);

  const handleChangeEditingStudent = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = e.target;
      setEditingStudent((prev) => (prev ? { ...prev, [name]: value } : prev));
    },
    []
  );

  const openErrorModal = useCallback(
    (
      errors: {
        student: CSVStudent;
        error: string;
        is_restore?: boolean;
        id: string;
      }[]
    ) => {
      setErrorDetails((prevErrorDetails) => {
        const updatedErrorDetails: ErrorDetail[] = errors.map((error) => {
          const existingError = prevErrorDetails.find(
            (ed) => ed.id === error.id
          );
          return {
            ...error,
            restored: existingError ? existingError.restored : false,
          };
        });
        return updatedErrorDetails;
      });
      setShowErrorModal(true);
    },
    []
  );

  const closeErrorModal = useCallback(() => {
    setShowErrorModal(false);
  }, []);

  const downloadExampleCSV = () => {
    const exampleCSV =
      "Nome,E-mail\nExemplo1,Exemplo1@example.com\nExemplo2,Exemplo2@example.com";
    const blob = new Blob([exampleCSV], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "modelo-exemplo.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <>
      <Modal show onHide={onClose} size="xl" dialogClassName="modal-90w">
        <Modal.Header closeButton>
          <Modal.Title>Importar alunos</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <ImportHistoryTable
            importHistory={importHistory}
            isFetchingHistory={isFetchingHistory}
            openErrorModal={openErrorModal}
          />
          <Form.Group>
            <Form.Label>
              Arquivo .csv <span className="text-danger">*</span>
            </Form.Label>
            <div className="d-flex gap-2 align-items-center mb-2">
              <Button
                variant="outline-secondary"
                onClick={() => document.getElementById("csvFileInput")?.click()}
              >
                Escolher arquivo
              </Button>
              {selectedFile && (
                <span className="text-muted">{selectedFile.name}</span>
              )}
              <Form.Control
                type="file"
                accept=".csv"
                key={fileInputKey}
                onChange={(e) => {
                  const input = e.target as HTMLInputElement;
                  if (input.files && input.files[0]) {
                    handleCSVStudentFileUpload(input.files[0]);
                  }
                }}
                style={{ display: "none" }}
                id="csvFileInput"
                data-testid="csvFileInput"
              />
            </div>
            <Form.Text className="text-muted">
              <strong>Padrão:</strong> O arquivo .csv não deve ter cabeçalho
              (nome das colunas). As colunas devem ser separadas por "," no
              seguinte formato:
              <br />
              Nome, E-mail
              <br />
              <Button variant="link" onClick={downloadExampleCSV}>
                Baixar modelo de exemplo
              </Button>
            </Form.Text>
          </Form.Group>

          {CSVStudentError && (
            <Alert variant="danger" className="mt-3">
              <strong>Erros encontrados:</strong>
              <pre className="mb-0 mt-2 small">{CSVStudentError}</pre>
            </Alert>
          )}

          {processCSVStudentError && (
            <Alert variant="danger" className="mt-3">
              <strong>Erro no processamento:</strong>
              <pre className="mb-0 mt-2 small">
                {processCSVStudentError.message}
              </pre>
            </Alert>
          )}

          <Form.Group className="mt-4">
            <Form.Label>Pesquisar alunos</Form.Label>
            <Form.Control
              type="text"
              placeholder="Pesquisar por nome ou email"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </Form.Group>

          <CSVStudentPreviewTable
            data={CSVStudentPreviewData}
            searchTerm={searchTerm}
            handleRemovePreviewItem={handleRemovePreviewItem}
          />

          <InvalidDataTable
            data={invalidData}
            searchTerm={searchTerm}
            editingIndex={editingIndex}
            editingStudent={editingStudent}
            handleEditInvalidItem={handleEditInvalidItem}
            handleRemoveInvalidItem={handleRemoveInvalidItem}
            handleChangeEditingStudent={handleChangeEditingStudent}
            handleSaveEdit={handleSaveEdit}
            handleCancelEdit={handleCancelEdit}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="primary"
            onClick={handleProcessImportInBackground}
            disabled={
              CSVStudentPreviewData.length === 0 || isProcessingCSVStudent
            }
          >
            {isProcessingCSVStudent ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  className="me-2"
                />
                IMPORTANDO
              </>
            ) : (
              "IMPORTAR"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <ErrorDetailsModal
        show={showErrorModal}
        onHide={closeErrorModal}
        errorDetails={errorDetails}
        token={token}
        onStudentRestored={handleStudentRestored}
      />

      {processError && (
        <Alert
          variant="danger"
          className="mt-3"
          onClose={() => setProcessError(null)}
          dismissible
        >
          {processError}
        </Alert>
      )}
    </>
  );
}

export default CSVStudentManagementView;
